<template>
  <div v-if="documentMenu.length > 0">
    <AppTooltip>
      <template #activator="{props: tooltipProps}">
        <div v-bind="tooltipProps">
          <v-menu location="left" :data-testid="`document-${document.id}-menu`">
            <template #activator="{ props }">
              <app-button type="icon" v-bind="props" data-locator="other-actions-menu-button">
                <app-material-icon name="more_horiz" color="inherit" />
              </app-button>
            </template>
            <v-list
              :data-locator="`documents-${document.type}-menu`"
              :data-testid="`document-${document.id}-menu-list`"
            >
              <template v-for="(item, i) in documentMenu">
                <v-list-item
                  v-if="item.text !== 'divider'"
                  :key="`menuItem-${i}`"
                  :data-testid="`${item.action}-menu-item-${document.id}`"
                  :data-locator=item.locatorValue
                  @click="handleMenuAction(item.action)"
                  :disabled="item.disabled"
                >
                  <div class="d-flex align-center">
                    <app-material-icon
                      v-if="item.icon"
                      :name="item.icon"
                      size="sm"
                      class="mr-2"
                    />
                    <img
                      v-else-if="item.img"
                      :src="item.img"
                      :alt="item.text"
                      class="mr-2"
                      width="20"
                      height="20"
                    />
                    <span>{{ item.text }}</span>
                    <AppFeatureTag
                      v-if="item.featureTag"
                      class="ml-2"
                      :tag="item.featureTag"
                      size="sm"
                    />
                  </div>
                </v-list-item>
                <v-divider v-if="item.text === 'divider'" :key="`divider-${i}`" />
              </template>
              <PolyOfficeListItemExport
                v-if="polyOfficeEnabled && document.type === 'file'"
                :fileSizes="[document.size]"
                btnHasIconAndText
                @polyOfficeOpenDialog="polyOfficeOpenDialogHandler"
              />
              <ArchiveAddAccessListItem
                v-if="document.type === 'file'"
                :routeOptions="archiveAddRouteOptions"
                btnHasIconAndText
              />
            </v-list>
          </v-menu>
        </div>
      </template>
      <span>{{ $t('common.additionalActions') }}</span>
    </AppTooltip>

    <!-- DIALOGS -->
    <IManageFilePickerDialog
      v-model:is-open="imanageFilePickerDialogIsOpen"
      @files="preparePostFildersAsVersionToImanage"
    />
    <IManageFolderPickerDialog
      v-if="imanageFolderPickerDialogIsOpen"
      v-model:is-open="imanageFolderPickerDialogIsOpen"
      @folders="preparePostFildersToImanage"
    />
    <DocumentsNumberingDialog v-if="numberingDialogIsOpen"
                              :document="document"
                              @close="numberingDialogIsOpen = false"
                              @onSuccess="refreshDocuments"
    />
    <DocumentsNotRedactableDialog
      v-if="showDocumentNotRedactableDialog"
      :reasons="filteredRedactionBlockedReasons"
      @close="documentNotRedactableDialogIsOpen = false"
    />
    <RedactStorageWarningDialog
      v-if="showRedactStorageWarningDialog"
      @cancel="showRedactStorageWarningDialog = false"
      @confirm="onConfirmRedactStorageWarning"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import aiAssistantIcon from '@/assets/img/ai_assistant_ico.svg'
import imanageIcon from '@/assets/img/icons/iManage.svg'
import AppFeatureTag from '@/common/AppFeatureTag.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import ArchiveAddAccessListItem from '@/common/archives/ArchiveAddAccessListItem.vue'
import IManageFilePickerDialog from '@/common/imanage/IManageFilePickerDialog.vue'
import IManageFolderPickerDialog from '@/common/imanage/IManageFolderPickerDialog.vue'
import { getPdfContentType, USER_ROLE, track } from '@/common/pendo/agent'
import { DocumentsExport } from '@/common/polyOffice/polyOffice.model'
import PolyOfficeListItemExport from '@/common/polyOffice/PolyOfficeListItemExport.vue'
import { getFileExtension, isFileSignable, openFileViewer } from '@/common/utils/files'
import { toKebabCase } from '@/common/utils/strings'
import { toast } from '@/common/utils/toast'
import { getUserSetting, setUserSetting } from '@/common/utils/userSettings'
import DocumentsNotRedactableDialog from '@/project/documents/dialogs/DocumentsNotRedactableDialog.vue'
import DocumentsNumberingDialog from '@/project/documents/dialogs/DocumentsNumberingDialog.vue'
import RedactStorageWarningDialog from '@/project/documents/dialogs/RedactStorageWarningDialog.vue'
import { SIGNING_CHECKLIST_ADD_ROUTE_NAME } from '@/router/routerData'
import {
  POST_FILDERS_AS_VERSION_TO_IMANAGE,
  GET_FOLDER_PATH,
  SET_TABLE_DOCUMENTS_PANE,
  SET_DOCUMENTS_VERSIONING_PANE,
} from '@/store/modules/documents/action_types'
import { DocumentsDeleteActionTypes } from '@/store/modules/documents-delete/action_types'
import { OPEN_DOCUMENT_RENAMING_DIALOG } from '@/store/modules/documents-rename/action_types'
import { POST_FILDERS_TO_IMANAGE } from '@/store/modules/imanage/action_types'
import { GET_EXPORT_IFRAME } from '@/store/modules/poly-office/action_types'
import { useLogsStore } from '@/stores/logsStore'

const REDACT_WARNING_KEY = 'hide-redact-storage-warning'

const IGNORED_REDACTION_BLOCKED_REASONS = [
  'NOT_PM',
  'NOT_DATAROOM',
  'MISSING_SUBSCRIPTION_OPTION',
]

export default {
  name: 'DocumentsTableItemMenu',
  components: {
    AppFeatureTag,
    AppTooltip,
    ArchiveAddAccessListItem,
    DocumentsNotRedactableDialog,
    DocumentsNumberingDialog,
    IManageFilePickerDialog,
    IManageFolderPickerDialog,
    PolyOfficeListItemExport,
    RedactStorageWarningDialog,
  },
  props: {
    document: {
      type: Object,
      required: true,
    },
  },
  setup: () => {
    const logsStore = useLogsStore()
    return { logsStore }
  },
  data () {
    return {
      imanageFilePickerDialogIsOpen: false,
      imanageFolderPickerDialogIsOpen: false,
      numberingDialogIsOpen: false,
      documentNotRedactableDialogIsOpen: false,
      showRedactStorageWarningDialog: false,
      skipRedactWarningInViewer: false,
    }
  },
  inject: ['loadDocumentsTable'],
  computed: {
    ...mapGetters('imanage', ['imanageEnabledAndLogged']),
    ...mapGetters('documents', ['isSpecialFolderContext', 'isRecentFolder']),
    ...mapState('room', ['currentRoom']),
    ...mapGetters('room', ['currentUserRights', 'isDataRoom', 'isStorageFull', 'roomMnemo', 'maxSignableSize']),
    ...mapGetters('user', ['polyOfficeEnabled']),
    isPm () {
      return this.$store.getters['room/isCurrentUserPm']
    },
    shouldShowRedactAction() {
      return !this.document.redactionBlockedReasons?.some(
        reason => IGNORED_REDACTION_BLOCKED_REASONS.includes(reason),
      )
    },
    filteredRedactionBlockedReasons() {
      return this.document.redactionBlockedReasons.filter(
        reason =>
          !IGNORED_REDACTION_BLOCKED_REASONS.includes(reason) &&
          // Do not show NO_PAGES reason for non-PDF document
          (reason !== 'NO_PAGES' || getFileExtension(this.document) === 'pdf'),
      )
    },
    showDocumentNotRedactableDialog() {
      return this.documentNotRedactableDialogIsOpen && this.filteredRedactionBlockedReasons.length > 0
    },
    documentMenu () {
      const menu = []

      const addMenuItem = ({ textKey, action, locatorValue, icon = null, img = null, featureTag = null, disabled = null }) => {
        menu.push({
          text: this.$t(textKey),
          action,
          locatorValue,
          ...(icon && { icon }),
          ...(img && { img }),
          ...(featureTag && { featureTag }),
          ...(disabled && { disabled }),
        })
      }

      const addDivider = () => {
        menu.push({ text: 'divider' })
      }

      const addFolderMenuItems = () => {
        if (!this.isSpecialFolderContext && this.document.canWrite) {
          const folderActionIcons = { move: 'subdirectory_arrow_right', rename: 'edit', delete: 'delete' };
          ['move', 'rename', 'delete'].forEach(action =>
            addMenuItem({
              textKey: `project.documents.table.DocumentsTableItemMenu.${action}`,
              action,
              locatorValue: `${action}-menu-item`,
              icon: folderActionIcons[action],
            }),
          )
        }
        if (this.isPm) {
          addMenuItem({ textKey: 'project.documents.table.DocumentsTableItemMenu.share', action: 'share', locatorValue: 'share-menu-item', icon: 'group' })

          if (this.isDataRoom) {
            addMenuItem({ textKey: 'project.documents.table.DocumentsTableItemMenu.seeLogs', action: 'logs', locatorValue: 'display-logs-menu-item', icon: 'browse_activity' })
          }

          if (this.document.numbering) {
            addMenuItem({ textKey: 'project.documents.table.DocumentsTableItemMenu.numbering', action: 'numbering', locatorValue: 'modify-numbering-menu-item', icon: 'format_list_numbered' })
          }

          if (this.imanageEnabledAndLogged) {
            addMenuItem({ textKey: 'project.documents.table.DocumentsTableItemMenu.exportToImanage', action: 'export-folder-to-imanage', locatorValue: 'export-to-imanage-menu-item', img: imanageIcon })
          }
        }
      }

      const addFileMenuItems = () => {
        addMenuItem({ textKey: 'project.documents.table.DocumentsTableItemMenu.preview', action: 'preview', locatorValue: 'preview-menu-item', icon: 'visibility' })

        if (this.isPm) {
          addMenuItem({ textKey: 'project.documents.table.DocumentsTableItemMenu.share', action: 'share', locatorValue: 'share-menu-item', icon: 'group' })
        }

        if (this.$vuetify.display.mdAndUp && this.shouldShowRedactAction) {
          addMenuItem({ textKey: 'project.documents.table.DocumentsTableItemMenu.redact', action: 'redact', locatorValue: 'redact-menu-item', icon: 'ink_eraser', featureTag: 'new' })
        }

        if (this.isPm || this.currentUserRights.canUploadVersions) {
          addMenuItem({ textKey: 'project.documents.table.DocumentsTableItemMenu.manageVersions', action: 'manage-versions', locatorValue: 'manage-versions-menu-item', icon: 'history' })
        } else if (this.document.nbVersions > 1) {
          addMenuItem({ textKey: 'project.documents.table.DocumentsTableItemMenu.showVersions', action: 'show-versions', locatorValue: 'show-versions-menu-item', icon: 'history' })
        }

        if (this.isPm && this.document.numbering) {
          addMenuItem({ textKey: 'project.documents.table.DocumentsTableItemMenu.numbering', action: 'numbering', locatorValue: 'modify-numbering-menu-item', icon: 'format_list_numbered' })
        }

        if (this.document.canResume) {
          addMenuItem({
            textKey: 'project.documents.table.DocumentsTableItemMenu.summary',
            action: 'summary',
            locatorValue: 'ia-summary-menu-item',
            img: aiAssistantIcon,
            featureTag: 'new',
          })
        }

        if (this.isRecentFolder && this.document.isNew) {
          addMenuItem({ textKey: 'project.documents.table.DocumentsTableItemMenu.openInFolder', action: 'open-in-folder', locatorValue: 'open-in-folder-menu-item', icon: 'folder_open' })
        }

        if (this.isPm && this.isDataRoom) {
          addMenuItem({ textKey: 'project.documents.table.DocumentsTableItemMenu.seeLogs', action: 'logs', locatorValue: 'display-logs-menu-item', icon: 'browse_activity' })
        }

        if (this.isPm && isFileSignable(this.document, this.maxSignableSize) && !this.currentRoom.isDataroom) {
          addDivider()
          addMenuItem({ textKey: 'project.documents.table.DocumentsTableItemMenu.signingChecklistAdd', action: 'signing-checklist-add', locatorValue: 'add-to-signing-checklist-menu-item', icon: 'signature' })
        }

        if (this.document.canWrite) {
          addDivider()
          const fileActionIcons = { move: 'subdirectory_arrow_right', duplicate: 'content_copy', rename: 'edit', delete: 'delete' };
          ['move', 'duplicate', 'rename', 'delete'].forEach(action =>
            addMenuItem({
              textKey: `project.documents.table.DocumentsTableItemMenu.${action}`,
              action,
              locatorValue: `${action}-menu-item`,
              icon: fileActionIcons[action],
              disabled: action === 'duplicate' ? this.isDataRoom && this.isStorageFull : null,
            }),
          )
        }

        if (this.imanageEnabledAndLogged) {
          [{ name: 'exportToImanage', locatorValue: 'imanage-export-menu-item' }, { name: 'exportToImanageAsVersion', locatorValue: 'imanage-export-as-version-menu-item' }].forEach(action =>
            addMenuItem({ textKey: `project.documents.table.DocumentsTableItemMenu.${action.name}`, action: toKebabCase(action.name), locatorValue: action.locatorValue, img: imanageIcon }),
          )
        }
      }

      if (this.document.type === 'folder') {
        addFolderMenuItems()
      } else if (this.document.type === 'file') {
        addFileMenuItems()
      }
      return menu
    },
    archiveAddRouteOptions () {
      return {
        params: {
          files: [this.document],
        },
        query: {
          fileIds: this.document.id,
          fromMnemo: this.roomMnemo,
        },
      }
    },
  },
  methods: {
    ...mapActions('documents', [
      SET_TABLE_DOCUMENTS_PANE,
      SET_DOCUMENTS_VERSIONING_PANE,
      POST_FILDERS_AS_VERSION_TO_IMANAGE,
      GET_FOLDER_PATH,
    ]),
    ...mapActions('documentsDelete', [DocumentsDeleteActionTypes.OPEN_DOCUMENTS_DELETION_DIALOG]),
    ...mapActions('documentsRename', [OPEN_DOCUMENT_RENAMING_DIALOG]),
    ...mapActions('imanage', [POST_FILDERS_TO_IMANAGE]),
    ...mapActions('polyOffice', [GET_EXPORT_IFRAME]),
    ...mapMutations('documentsAISummary', ['SET_DOCUMENT_TO_SUMMARIZE_WITH_AI']),
    handleMenuAction (action) {
      switch (action) {
        case 'preview': {
          this.onOpenDocument()
          break
        }
        case 'redact': {
          if (!this.document.isRedactable) {
            this.documentNotRedactableDialogIsOpen = true
            return
          }
          const hideWarning = getUserSetting(this.$store.getters['user/currentUserId'], REDACT_WARNING_KEY)
          if (!hideWarning) {
            this.showRedactStorageWarningDialog = true
            this.skipRedactWarningInViewer = true
            return
          }
          this.onOpenDocument(true)
          break
        }
        case 'share':
          this.openPane('share')
          break
        case 'move':
          this.openPane('move')
          break
        case 'duplicate':
          this.openPane('duplicate')
          break
        case 'manage-versions':
          this.openVersioningPane('manage')
          break
        case 'show-versions':
          this.openVersioningPane('history')
          break
        case 'logs':
          this.logsStore.openLogsDialog(this.document)
          break
        case 'delete':
          this.OPEN_DOCUMENTS_DELETION_DIALOG([this.document])
          break
        case 'rename':
          this.OPEN_DOCUMENT_RENAMING_DIALOG(this.document)
          break
        case 'signing-checklist-add':
          this.$router.push({
            name: SIGNING_CHECKLIST_ADD_ROUTE_NAME,
            state: {
              files: [this.document],
            },
            query: {
              fileIds: this.document.id,
            },
          })
          break
        case 'export-folder-to-imanage':
          this.imanageFolderPickerDialogIsOpen = true
          break
        case 'export-to-imanage':
          this.imanageFolderPickerDialogIsOpen = true
          break
        case 'export-to-imanage-as-version':
          this.imanageFilePickerDialogIsOpen = true
          break
        case 'numbering':
          this.numberingDialogIsOpen = true
          break
        case 'open-in-folder':
          this.$emit('openInFolder', this.document)
          break
        case 'summary':
          this.SET_DOCUMENT_TO_SUMMARIZE_WITH_AI(this.document)
          break
      }
    },
    onConfirmRedactStorageWarning(hideChecked) {
      this.showRedactStorageWarningDialog = false
      if (hideChecked) {
        setUserSetting(this.$store.getters['user/currentUserId'], REDACT_WARNING_KEY, true)
      }
      this.onOpenDocument(true)
    },
    onOpenDocument(isRedactMode = false) {
      const query = {}
      if (isRedactMode) {
        query.isRedactMode = true
      }
      if (this.skipRedactWarningInViewer) {
        query.skipRedactWarning = true
      }
      track('document_previewed', {
        document_id: this.document.id,
        project_mnemo: this.roomMnemo,
        user_role: this.$store.getters['room/isCurrentUserPm'] ? USER_ROLE.PROJECT_MANAGER : USER_ROLE.GUEST,
        document_type: this.document.ext,
        entrypoint: 'context_menu',
        pdf_content_type: getPdfContentType(this.document),
      })
      openFileViewer(this.roomMnemo, this.document.id, query)
      this.skipRedactWarningInViewer = false
    },
    openPane (tab) {
      this.SET_TABLE_DOCUMENTS_PANE({ isOpen: true, documents: [this.document], tab, forcePmSelection: true })
    },
    openVersioningPane (tab) {
      this.SET_DOCUMENTS_VERSIONING_PANE({ isOpen: true, documents: [this.document], tab })
    },
    async preparePostFildersToImanage (target) {
      const data = {
        targetId: target.selected,
      }
      if (this.document.type === 'file') {
        data.files = [this.document.id]
      } else if (this.document.type === 'folder') {
        data.folders = [this.document.id]
      }
      try {
        await this.POST_FILDERS_TO_IMANAGE({
          mnemo: this.roomMnemo,
          data,
        })
        toast.success(this.$t('project.documents.table.DocumentsTableItemMenu.postFildersToIManageSuccess'))
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.documents.table.DocumentsTableItemMenu.postFildersToIManageError'))
      }
    },
    async preparePostFildersAsVersionToImanage (target) {
      const data = {
        targetId: target.selected,
      }
      if (this.document.type === 'file') {
        data.fileId = this.document.id
      }
      try {
        await this.POST_FILDERS_AS_VERSION_TO_IMANAGE({
          mnemo: this.roomMnemo,
          data,
        })
        toast.success(this.$t('project.documents.table.DocumentsTableItemMenu.postFildersToIManageSuccess'))
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.documents.table.DocumentsTableItemMenu.postFildersToIManageError'))
      }
    },
    refreshDocuments () {
      this.loadDocumentsTable()
    },
    async polyOfficeOpenDialogHandler () {
      const payload = new DocumentsExport(
        this.roomMnemo,
        [this.document.id],
      )
      try {
        await this.GET_EXPORT_IFRAME({
          payload,
          successMessage: this.$t('project.documents.table.DocumentsTableItemMenu.polyOffice.exportSuccessful'),
        })
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.documents.table.DocumentsTableItemMenu.polyOffice.exportFailed'))
      }
    },
  },
}
</script>
